// @flow
import React, { useState, useEffect } from "react";
import "./styles.css";
import FAQ from "../../models/faq";
import { Card } from "../card";
import TextBlock from "../text-block";
import * as R from "ramda";
import FAB from "../fab";
import IconButton from "../icon-button";
import classNames from "classnames";
import Spinner from "../spinner";

type PropsType = { onSave: any => void, onDelete: any=>void, faqs: { [string]: FAQ } };

export const FAQEditor = ({ onSave, onDelete, faqs }: PropsType): React$Element => {

    const [myFAQs, setMyFAQs] = useState([]);
    const [hasDeletions, setHasDeletions] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const newFAQ = (): void => {
        setMyFAQs([].concat({ ...FAQ.create(), isDirty: true }, myFAQs));
    };

    const saveFAQs = (): void => {
        setIsBusy(true);
        myFAQs.forEach(faq => {
            if (faq.isDirty) {
                onSave(FAQ.create(faq));
            }
        });
        const newFAQIds = myFAQs.map(faq => faq.id);
        Object.keys(faqs)
            .filter(id => newFAQIds.indexOf(id) === -1)
            .forEach(id => {
                onDelete(id);
            });
    };

    const removeFAQ = (index: number) => () => {
        // eslint-disable-next-line no-alert
        if (window.confirm("Are you sure you want to delete this FAQ?")) {
            const newFAQs= [].concat(myFAQs.slice(0, index)).concat(myFAQs.slice(index + 1));
            setMyFAQs(newFAQs);
            setHasDeletions(true);
        }
    };

    const handleFAQStateChange = R.curry((index: number, key: string, value: string): void => {
        const newMyFAQs = [].concat(myFAQs.slice(0, index), {
            ...myFAQs[index],
            [key]: value,
            isDirty: true
        }, myFAQs.slice(index + 1));
        setMyFAQs(newMyFAQs);
    });

    const shiftFAQ = R.curry((index: number, shift: number) => (shift > 0)
        ? () => {
            const faqCount = (myFAQs || []).length;
            if (faqCount > 1 && index < faqCount - 1) {
                const cloned = R.clone(myFAQs);
                const foo = cloned[index];
                cloned[index] = cloned[index + 1];
                cloned[index + 1] = foo;
                setMyFAQs(cloned.map((faq, i) => ({
                    ...faq,
                    sortOrder: i,
                    isDirty: true
                })).sort((a, b) => a.sortOrder - b.sortOrder));
            }
        }
        : () => {
            const faqCount = (myFAQs || []).length;
            const cloned = R.clone(myFAQs);
            if (faqCount > 1 && index > 0) {
                const foo = cloned[index];
                cloned[index] = cloned[index - 1];
                cloned[index - 1] = foo;
                setMyFAQs(cloned.map((faq, i) => ({
                    ...faq,
                    sortOrder: i,
                    isDirty: true
                })).sort((a, b) => a.sortOrder - b.sortOrder));
            }
        }
    );

    const faqCount = Object.keys(myFAQs);
    const faqList = Object.values(myFAQs || {}).map((faq, i) => (
        <Card className={ "faq-editor--faq" } key={ i }>
            <header className="faq-editor--faq-header">
                <span>
                    { (i <= 0)
                        ? null
                        : (
                            <IconButton onClick={ shiftFAQ(i)(-1) } label={ "MOVE UP" } icon={ "faAngleDoubleUp" }/>
                        )
                    }
                    { (i + 1 >= faqCount)
                        ? null
                        : (
                            <IconButton
                                onClick={ shiftFAQ(i)(1) }
                                label={ "MOVE DOWN" }
                                icon={ "faAngleDoubleDown" }/>
                        ) }
                </span>
                <span>
                    <IconButton onClick={ removeFAQ(i) } label={ "DELETE" } icon={ "faTrashAlt" }/>
                </span>
            </header>
            <div>
                <TextBlock
                    labelText={ "Question" }
                    onChange={ handleFAQStateChange(i)("question") }
                    value={ faq.question }/>
                <TextBlock
                    labelText={ "Answer" }
                    onChange={ handleFAQStateChange(i)("answer") }
                    value={ faq.answer }/>
            </div>
        </Card>
    ));

    useEffect(() => {
        const newFAQs = Object.entries(faqs).map(entry => FAQ.create(entry[1], entry[0])).sort((a, b) => a.sortOrder - b.sortOrder);
        setMyFAQs(newFAQs);
        setHasDeletions(false);
        setIsBusy(false);
    }, [faqs]);

    const hasDirty = myFAQs.some(faq => faq.isDirty);
    return (
        <div className={ "faq-editor" }>
            <header className={ "faq-editor--faqs-header" }>
                <FAB
                    className={ "faq-editor--header-add-button" }
                    icon={ "faPlusCircle" }
                    onClick={ newFAQ }/>
                <FAB
                    className={ "faq-editor--header-add-button" }
                    icon={ "faSave" }
                    onClick={ saveFAQs }
                    disabled={ !hasDirty && !hasDeletions }/>
            </header>
            <div className={ "faq-editor--faqs" }>
                { faqList }
            </div>
            <div className={ classNames("faq-editor--is-busy", { active: isBusy }) }>
                <Spinner containerStyle={ { position: "absolute" } }/>
            </div>
        </div>

    );
};

